import React, { useCallback, useMemo, useState } from 'react';

import Dialog from 'components/dialog/components/Dialog';

import DialogContext, {
  DIALOG_INITIAL_STATE,
  DialogProviderState,
} from './contexts/DialogContext';

interface DialogProviderProps {
  children: React.ReactNode;
}

const DialogProvider = ({ children }: DialogProviderProps) => {
  const [dialogState, setDialogState] = useState(DIALOG_INITIAL_STATE);

  const showDialog = useCallback(
    ({
      title,
      size,
      content,
      fullScreen,
      disableEscapeKeyDown,
      enableBackdropClose,
      allowClose,
    }: DialogProviderState) => {
      setDialogState({
        open: true,
        title,
        size,
        content,
        fullScreen,
        disableEscapeKeyDown,
        enableBackdropClose,
        allowClose,
      });
    },
    []
  );

  const hideDialog = useCallback(() => {
    setDialogState((prevState) => ({ ...prevState, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      dialogState,
      showDialog,
      hideDialog,
    }),
    [dialogState, showDialog, hideDialog]
  );

  const setIsOpen = () => {
    if (!dialogState.open) {
      hideDialog();
    }
  };

  return (
    <DialogContext.Provider value={value}>
      <Dialog
        title={dialogState.title}
        size={dialogState.size}
        content={dialogState.content}
        fullScreen={dialogState.fullScreen}
        disableEscapeKeyDown={dialogState.disableEscapeKeyDown}
        enableBackdropClose={dialogState.enableBackdropClose}
        isOpen={Boolean(dialogState.open)}
        setIsOpen={setIsOpen}
        allowClose={dialogState.allowClose}
      />
      {children}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
